// Начален екран на наемателя: апартамент, дължим наем, падеж,
// неплатени сметки, IBAN на наемодателя, ИТН номера, чакащи протоколи.

import React, { useCallback, useState } from 'react';
import { Image, Linking, Pressable, ScrollView, Text, View } from 'react-native';
import { showError, showSuccess } from '../../toast';
import * as Clipboard from 'expo-clipboard';
import * as ImagePicker from 'expo-image-picker';
import { useFocusEffect } from '@react-navigation/native';
import { api, API_URL, money, monthLabel, UTILITY_LABELS } from '../../api';
import { showPhoto } from '../../photo-viewer';
import { Badge, Button, Card, COLORS, Input, Label, Muted, Row, Screen, SP, Title } from '../../ui';
import { cityLabel } from '../../geo';
import { compressAndUploadPhoto } from '../../photo-picker';
import { getLocale, t } from '../../i18n';

// „Платих по банка“ + (по избор) снимка на платежното нареждане.
// Снимката е ПРЕПОРЪЧИТЕЛНА — има ясен изход „Продължи без снимка“.
// Плащането НЕ става платено тук — наемодателят натиска „Получено“.
function BankSlipSection({ payment, reload }: { payment: any; reload: () => void }) {
  const [busy, setBusy] = useState(false);
  const declared = !!payment.bankDeclaredAt;

  const pick = async (fromCamera: boolean): Promise<string | null> => {
    const result = fromCamera
      ? await ImagePicker.launchCameraAsync({ quality: 0.7 })
      : await ImagePicker.launchImageLibraryAsync({ mediaTypes: ['images'], quality: 0.7 });
    if (result.canceled || !result.assets[0]) return null;
    return compressAndUploadPhoto(result.assets[0]);
  };

  const send = async (photoUrl: string | null) => {
    setBusy(true);
    try {
      const path = declared ? 'bank-slip' : 'bank-declare';
      const res = await api(`/payments/${payment.id}/${path}`, {
        body: photoUrl ? { photoUrl } : {},
      });
      const slip = res.slip;
      if (slip?.codeFound === true) {
        showSuccess(t('Кодът е открит в платежното. Наемодателят е известен.'));
      } else if (slip?.read && slip?.codeFound === false) {
        showError(
          t('Не откриваме кода {code} в основанието. Ако сте го пропуснали, плащането пак ще стигне, но наемодателят ще го засича по-трудно.', {
            code: payment.referenceCode ?? '',
          })
        );
      } else {
        showSuccess(t('Отбелязано. Наемодателят ще провери сметката си и ще потвърди.'));
      }
      reload();
    } catch (e: any) {
      showError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const withPhoto = async (fromCamera: boolean) => {
    setBusy(true);
    try {
      const url = await pick(fromCamera);
      if (!url) return;
      await send(url);
    } catch (e: any) {
      showError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const mismatch =
    payment.slipAmountCents != null && payment.slipAmountCents !== payment.amountCents ? payment.slipAmountCents : null;

  return (
    <View style={{ marginTop: 10, borderTopWidth: 1, borderTopColor: COLORS.border, paddingTop: 10 }}>
      {!declared ? (
        <>
          <Label>{t('Преведохте ли вече?')}</Label>
          <Muted>
            {t('📸 Със снимка наемодателят вижда веднага, че кодът е вписан, и потвърждава по-бързо.')}
          </Muted>
          <Button title={t('Платих — прилагам снимка')} onPress={() => withPhoto(false)} loading={busy} />
          <Button title={t('Снимай платежното 📷')} variant="secondary" onPress={() => withPhoto(true)} />
          <Button title={t('Продължи без снимка')} variant="secondary" onPress={() => send(null)} />
        </>
      ) : (
        <>
          <Badge text={t('Наредено — чака потвърждение от наемодателя')} color="#D97706" />
          {payment.slipPhotoUrl ? (
            <>
              <Pressable onPress={() => showPhoto(`${API_URL}${payment.slipPhotoUrl}`)}>
                <Image
                  source={{ uri: `${API_URL}${payment.slipPhotoUrl}` }}
                  style={{ width: 110, height: 110, borderRadius: 8, marginVertical: 6 }}
                />
              </Pressable>
              {payment.slipCodeFound === true && <Badge text={t('Кодът е открит в платежното ✓')} color={COLORS.success} />}
              {payment.slipCodeFound === false && (
                <>
                  <Badge text={t('Кодът не е открит в платежното')} color={COLORS.danger} />
                  <Muted>
                    {t('Ако сте го пропуснали, плащането пак ще стигне, но наемодателят ще го засича по-трудно.')}
                  </Muted>
                  <Button title={t('Качи друга снимка')} variant="secondary" onPress={() => withPhoto(false)} loading={busy} />
                </>
              )}
              {mismatch != null && (
                <Muted>
                  {t('⚠️ В платежното е {a}, а дължимото е {b}.', { a: money(mismatch), b: money(payment.amountCents) })}
                </Muted>
              )}
            </>
          ) : (
            <>
              <Muted>{t('Без приложено платежно. Можете да добавите снимката и сега.')}</Muted>
              <Button title={t('Добави снимка на платежното')} variant="secondary" onPress={() => withPhoto(false)} loading={busy} />
            </>
          )}
        </>
      )}
    </View>
  );
}

export default function TenantHomeScreen({ navigation }: any) {
  const [data, setData] = useState<any | undefined>(undefined);
  const [pastTenancies, setPastTenancies] = useState<any[]>([]);
  const [managerRestricted, setManagerRestricted] = useState(false);
  const [restrictedMessage, setRestrictedMessage] = useState('');
  const [mandateFlowId, setMandateFlowId] = useState<string | null>(null);
  const [remarks, setRemarks] = useState<Record<string, string>>({});
  // Няколко активни наема (напр. жилище в два града) → превключвател
  const [tenancies, setTenancies] = useState<any[]>([]);
  const [selectedTenancyId, setSelectedTenancyId] = useState<string | null>(null);

  const load = useCallback(() => {
    api(`/tenant/home${selectedTenancyId ? `?tenancyId=${selectedTenancyId}` : ''}`)
      .then((r) => {
        setData(r.tenancy);
        setTenancies(r.tenancies ?? []);
        if (r.tenancy?.id) setSelectedTenancyId(r.tenancy.id);
        setPastTenancies(r.pastTenancies ?? []);
        setManagerRestricted(!!r.managerRestricted);
        setRestrictedMessage(r.managerRestrictedMessage ?? '');
      })
      .catch(() => {});
  }, [selectedTenancyId]);
  useFocusEffect(load);

  if (data === undefined) return <Screen><Muted>{t('Зареждане…')}</Muted></Screen>;

  if (data === null) {
    // Акаунтът се запазва след напускане: история + архив остават достъпни
    return (
      <Screen>
        <Title>{t('Здравейте!')}</Title>
        <Card>
          <Muted>
            {t('В момента нямате активно наемно отношение. Акаунтът ви се запазва — когато наемодател добави номера ви, новият дом ще се появи тук автоматично.')}
          </Muted>
        </Card>

        {pastTenancies.length > 0 && (
          <Card>
            <Label>{t('Предишни квартири')}</Label>
            {pastTenancies.map((pt) => (
              <View key={pt.id} style={{ paddingVertical: 6, borderBottomWidth: 1, borderBottomColor: COLORS.border }}>
                <Text style={{ color: COLORS.text, fontWeight: '500' }}>{pt.address}, {pt.city}</Text>
                <Muted>
                  {t('{amount}/мес.', { amount: money(pt.monthlyRentCents) })} ·{' '}
                  {pt.startDate ? new Date(pt.startDate).toLocaleDateString(getLocale() === 'en' ? 'en-GB' : 'bg-BG') : '—'} —{' '}
                  {pt.endDate ? new Date(pt.endDate).toLocaleDateString(getLocale() === 'en' ? 'en-GB' : 'bg-BG') : '—'}
                </Muted>
                <Button
                  title={t('Оцени наемодателя ★')}
                  variant="secondary"
                  onPress={() => navigation.navigate('Review', { tenancyId: pt.id, targetName: `${pt.address}, ${pt.city}` })}
                />
              </View>
            ))}
            <Muted>{t('Историята ви е видима за бъдещи наемодатели и работи във ваша полза.')}</Muted>
          </Card>
        )}

        <Card>
          <Label>{t('Архивът ви остава достъпен')}</Label>
          <Muted>{t('Договори, бележки за платен наем и протоколи от предишни квартири.')}</Muted>
          <Button title={t('Моите документи →')} variant="secondary" onPress={() => navigation.navigate('Documents')} />
        </Card>
      </Screen>
    );
  }

  const nextPayment = data.duePayments[0];

  const confirmHandover = async (protocolId: string) => {
    try {
      await api(`/tenancies/handover/${protocolId}/confirm`, { body: {} });
      showSuccess(t('Потвърдихте протокола.'));
      load();
    } catch (e: any) {
      showError(e.message);
    }
  };

  const copy = async (text: string, label: string) => {
    await Clipboard.setStringAsync(text);
    showSuccess(t('{label} е копиран.', { label }));
  };

  const act = async (path: string, okMessage?: string) => {
    try {
      await api(path, { body: {} });
      if (okMessage) showSuccess(okMessage);
      load();
    } catch (e: any) {
      showError(e.message);
    }
  };

  // Подписване на мандат: банковите данни (IBAN) се въвеждат на защитената
  // страница на платежния оператор — приложението никога не ги вижда.
  const startMandate = async () => {
    try {
      const res = await api('/payments/sepa/mandate/start', { body: { tenancyId: data.id } });
      if (res.completed) {
        showSuccess(t('Съгласието е подписано. Наемът ще се тегли автоматично.'));
        load();
      } else if (res.redirectUrl) {
        setMandateFlowId(res.flowId);
        await Linking.openURL(res.redirectUrl);
      }
    } catch (e: any) {
      showError(e.message);
    }
  };

  const confirmMandate = async () => {
    try {
      await api('/payments/sepa/mandate/confirm', { body: { flowId: mandateFlowId, tenancyId: data.id } });
      setMandateFlowId(null);
      showSuccess(t('Съгласието е подписано. Наемът ще се тегли автоматично.'));
      load();
    } catch (e: any) {
      showError(e.message);
    }
  };

  return (
    <Screen>
      <Title>{t('Моят дом')}</Title>

      {/* Няколко активни наема → превключвател между жилищата */}
      {tenancies.length > 1 && (
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
          {tenancies.map((tn) => {
            const active = tn.id === data.id;
            return (
              <Pressable
                key={tn.id}
                onPress={() => setSelectedTenancyId(tn.id)}
                style={{
                  paddingVertical: 8,
                  paddingHorizontal: 14,
                  borderRadius: 999,
                  backgroundColor: active ? COLORS.primary : COLORS.card,
                  borderWidth: 1,
                  borderColor: active ? COLORS.primary : COLORS.border,
                }}
              >
                <Text style={{ color: active ? '#fff' : COLORS.text, fontWeight: '600' }}>
                  {tn.address}, {tn.city}
                </Text>
              </Pressable>
            );
          })}
        </View>
      )}

      {/* Отстранена нередност чака потвърждението МУ — червено, за да личи */}
      {data.issuesAwaitingConfirm > 0 && (
        <Pressable onPress={() => navigation.navigate('Issues')}>
          <Card style={{ borderColor: COLORS.danger, borderWidth: 2 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
              <View
                style={{
                  minWidth: 28,
                  height: 28,
                  borderRadius: 14,
                  paddingHorizontal: 8,
                  backgroundColor: COLORS.danger,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Text style={{ color: '#fff', fontWeight: '700' }}>{data.issuesAwaitingConfirm}</Text>
              </View>
              <View style={{ flex: 1 }}>
                <Text style={{ color: COLORS.text, fontWeight: '600' }}>
                  {data.issuesAwaitingConfirm === 1
                    ? t('Отстранена нередност чака потвърждение')
                    : t('Отстранени нередности чакат потвърждение')}
                </Text>
                <Muted>{t('Прегледайте резултата и потвърдете →')}</Muted>
              </View>
            </View>
          </Card>
        </Pressable>
      )}

      {managerRestricted && (
        <Card style={{ borderColor: COLORS.danger, borderWidth: 2 }}>
          <Badge text={t('⚠ Наемодателят не си е платил абонамента')} color={COLORS.danger} />
          <Muted>{restrictedMessage}</Muted>
        </Card>
      )}

      {/* ДЪЛЖИМ НАЕМ — най-важното за наемателя, най-отгоре (action-first) */}
      {nextPayment ? (
        <Card style={{ borderColor: `${COLORS.warn}55`, borderWidth: 1.5, backgroundColor: COLORS.warnSoft }}>
          <Text style={{ fontSize: 11, fontWeight: '800', letterSpacing: 0.6, color: COLORS.warn, textTransform: 'uppercase', marginBottom: 6 }}>
            {t('● Дължим наем · {month}', { month: monthLabel(nextPayment.periodMonth, nextPayment.periodYear) })}
          </Text>
          <View style={{ flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between' }}>
            <Text style={{ fontSize: 30, fontWeight: '800', color: COLORS.text }}>{money(nextPayment.amountCents)}</Text>
            <Text style={{ color: COLORS.warn, fontWeight: '700' }}>
              {t('до {date}', { date: new Date(nextPayment.dueDate).toLocaleDateString(getLocale() === 'en' ? 'en-GB' : 'bg-BG') })}
            </Text>
          </View>
          {nextPayment.referenceCode && data.rentCollectionMethod === 'BANK_TRANSFER' && !managerRestricted && (
            <Pressable
              onPress={() => copy(nextPayment.referenceCode, t('Кодът за основание'))}
              style={{ marginTop: 10, flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: COLORS.card, borderRadius: 10, borderWidth: 1, borderColor: COLORS.border, padding: 12 }}
            >
              <View style={{ flex: 1 }}>
                <Text style={{ fontSize: 11, color: COLORS.muted, fontWeight: '700' }}>{t('ОСНОВАНИЕ ЗА ПРЕВОДА')}</Text>
                <Text style={{ fontSize: 15, fontWeight: '700', color: COLORS.text }}>{nextPayment.referenceCode}</Text>
              </View>
              <Text style={{ color: COLORS.primary, fontWeight: '700' }}>{t('Копирай')}</Text>
            </Pressable>
          )}
        </Card>
      ) : (
        <Card style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
          <View style={{ width: 34, height: 34, borderRadius: 17, backgroundColor: COLORS.successSoft, alignItems: 'center', justifyContent: 'center' }}>
            <Text style={{ fontSize: 18 }}>✓</Text>
          </View>
          <Text style={{ flex: 1, color: COLORS.text, fontWeight: '600' }}>{t('Няма дължим наем — всичко е платено.')}</Text>
        </Card>
      )}

      <Card>
        <Text style={{ fontSize: 17, fontWeight: '700', color: COLORS.text }}>{data.apartment.address}</Text>
        <Muted>{cityLabel(data.apartment.city, data.apartment.region)}</Muted>
        <View style={{ height: SP.sm }} />
        <Row left={t('Наемодател')} right={data.manager.name} />
        <Row left={t('Телефон')} right={data.manager.phone} />
        <Row left={t('Месечен наем')} right={money(data.monthlyRentCents)} />
        <Row left={t('Падеж')} right={t('{day}-о число', { day: data.dueDay })} />
      </Card>

      {!managerRestricted && data.rentCollectionMethod === 'SEPA_DEBIT' && (
        <Card>
          <Label>{t('Автоматично теглене (SEPA)')}</Label>
          {data.gocardlessMandateId ? (
            <>
              <Muted>
                {t('Подписали сте съгласие — наемът и сметките се теглят сами на падежа. Получавате известие преди всяко теглене и виждате статуса тук.')}
              </Muted>
              {nextPayment && nextPayment.status !== 'PENDING' && (
                <Row left={t('Статус на тегленето')} right={
                  ({ SUBMITTED: t('Наредено'), COLLECTED: t('Изтеглено'), IN_TRANSIT: t('Пътува'), PAID: t('Получено') } as any)[
                    nextPayment.status
                  ] ?? nextPayment.status
                } />
              )}
            </>
          ) : mandateFlowId ? (
            <>
              <Muted>
                {t('Отворихме страницата на платежния оператор — там въвеждате име и IBAN и подписвате съгласието. Когато приключите, натиснете бутона.')}
              </Muted>
              <Button title={t('Довърших подписването ✓')} onPress={confirmMandate} />
            </>
          ) : (
            <>
              <Muted>
                {t('Наемодателят е избрал автоматично теглене. Подписвате еднократно съгласие (мандат) на защитената страница на платежния оператор — там въвеждате IBAN-а си, приложението не го вижда. После наемът се плаща сам всеки месец. Може да прекратите мандата по всяко време.')}
              </Muted>
              <Button title={t('Подписвам съгласие за теглене')} onPress={startMandate} />
            </>
          )}
        </Card>
      )}

      {!managerRestricted && data.rentCollectionMethod === 'CASH' && nextPayment && (
        <Card>
          <Label>{t('Плащане в брой')}</Label>
          {nextPayment.status === 'AWAITING_CONFIRMATION' ? (
            nextPayment.cashInitiatedBy === 'TENANT' ? (
              <Muted>{t('Отбелязахте „платих в брой“ — чака потвърждение от наемодателя.')}</Muted>
            ) : (
              <>
                <Muted>{t('Наемодателят отбеляза, че е получил наема в брой. Потвърждавате ли?')}</Muted>
                <Button title={t('Потвърждавам ✓')} onPress={() => act(`/payments/${nextPayment.id}/cash-confirm`)} />
              </>
            )
          ) : (
            <>
              <Muted>{t('След като платите на ръка, отбележете тук — наемодателят ще потвърди и ще получите бележка.')}</Muted>
              <Button title={t('Платих в брой')} onPress={() => act(`/payments/${nextPayment.id}/cash-initiate`)} />
            </>
          )}
        </Card>
      )}

      {(data.rentCollectionMethod === 'BANK_TRANSFER' || managerRestricted) &&
        (data.manager.iban || nextPayment?.referenceCode) && (
        <Card>
          <Label>{t('Плащане по банка')}</Label>
          {data.manager.iban && (
            <>
              <Row left="IBAN" right={data.manager.iban} />
              {!managerRestricted && (
                <Button title={t('Копирай IBAN')} variant="secondary" onPress={() => copy(data.manager.iban, 'IBAN')} />
              )}
            </>
          )}
          {nextPayment?.referenceCode && (
            <>
              <Row left={t('Основание')} right={nextPayment.referenceCode} />
              {!managerRestricted && (
                <>
                  <Button
                    title={t('Копирай основанието')}
                    variant="secondary"
                    onPress={() => copy(nextPayment.referenceCode, t('Кодът за основание'))}
                  />
                  <Muted>{t('Въведете този код като основание на превода — така плащането се разпознава автоматично.')}</Muted>
                </>
              )}
            </>
          )}
          {managerRestricted && (
            <Muted>{t('Данните са скрити, докато наемодателят не поднови абонамента си.')}</Muted>
          )}

          {/* След превода: отбелязване + (по избор) снимка на платежното */}
          {!managerRestricted && nextPayment && (
            <BankSlipSection payment={nextPayment} reload={load} />
          )}
        </Card>
      )}

      <Card>
        <Label>{t('Неплатени сметки')}</Label>
        {data.unpaidBills.length === 0 && <Badge text={t('Няма неплатени сметки')} color={COLORS.success} />}
        {data.unpaidBills.map((b: any) => (
          <Row
            key={b.id}
            left={`${t(UTILITY_LABELS[b.type])} · ${monthLabel(b.periodMonth, b.periodYear)}`}
            right={money(b.amountCents)}
          />
        ))}
      </Card>

      {data.apartment.utilityAccounts.length > 0 && (
        <Card>
          <Label>{t('ИТН / клиентски номера')}</Label>
          {data.apartment.utilityAccounts.map((u: any) => (
            <Row
              key={u.id}
              left={`${t(UTILITY_LABELS[u.type])}${u.providerName ? ` (${u.providerName})` : ''}`}
              right={u.clientNumber}
            />
          ))}
        </Card>
      )}

      {data.pendingHandovers.map((p: any) => (
        <Card key={p.id} style={{ borderColor: COLORS.primary, borderWidth: 2 }}>
          <Label>{p.type === 'MOVE_IN' ? t('Приемо-предавателен протокол (настаняване)') : t('Приемо-предавателен протокол (напускане)')}</Label>
          <Muted>
            {t('Снимките документират състоянието, в което се предава апартаментът. Докоснете снимка за пълен размер и увеличение. С потвърждението приемате имота в този вид (записва се точният час).')}
          </Muted>
          <ScrollView horizontal style={{ marginVertical: 10 }}>
            {p.photos.map((photo: string) => (
              <Pressable key={photo} onPress={() => showPhoto(`${API_URL}${photo}`)}>
                <Image
                  source={{ uri: `${API_URL}${photo}` }}
                  style={{ width: 130, height: 130, borderRadius: 10, marginRight: 8, backgroundColor: COLORS.border }}
                />
              </Pressable>
            ))}
          </ScrollView>
          {p.notes ? <Muted>{t('Бележки на наемодателя: {notes}', { notes: p.notes })}</Muted> : null}

          {p.tenantRemark ? (
            <View style={{ marginTop: 8, padding: 10, backgroundColor: COLORS.bg, borderRadius: 8 }}>
              <Text style={{ color: COLORS.text }}>{t('Вашата забележка: „{text}“', { text: p.tenantRemark })}</Text>
              <Badge
                text={p.remarkAcceptedAt ? t('Приета от наемодателя ✓') : t('Чака приемане от наемодателя')}
                color={p.remarkAcceptedAt ? COLORS.success : '#D97706'}
              />
            </View>
          ) : null}
          {!p.remarkAcceptedAt && (
            <>
              <Label>{t('Забележка по състоянието (по избор)')}</Label>
              <Input
                value={remarks[p.id] ?? ''}
                onChangeText={(v) => setRemarks((r) => ({ ...r, [p.id]: v }))}
                multiline
                placeholder={t('напр. драскотина на паркета в хола, петно на дивана…')}
              />
              <Button
                title={p.tenantRemark ? t('Промени забележката') : t('Изпрати забележка')}
                variant="secondary"
                onPress={async () => {
                  try {
                    await api(`/tenancies/handover/${p.id}/remark`, { body: { text: remarks[p.id] } });
                    showSuccess(t('Забележката е изпратена към наемодателя.'));
                    setRemarks((r) => ({ ...r, [p.id]: '' }));
                    load();
                  } catch (e: any) {
                    showError(e.message);
                  }
                }}
                disabled={!remarks[p.id]?.trim()}
              />
            </>
          )}

          <Button title={t('Потвърждавам състоянието ✓')} onPress={() => confirmHandover(p.id)} />
        </Card>
      ))}
    </Screen>
  );
}
